import { Box, Card, VStack } from '@chakra-ui/react'
import { type ReactNode } from 'react'

import { Heading } from '@/components/atoms/Heading'
import { UserStackedAvatars } from '@/components/molecules/User/UserStackedAvatars'
import { useColorModeValue } from '@/components/ui/color-mode'
import type { NetworkLogoFragment } from '@/graphql/api/generated'

import { NetworkLogo } from '../../User/Network/NetworkLogo'

export type NetworkCardPresentationStackedUser = {
  id?: string
  fullname: string
  roles: string[]
  avatar?: { contentUrl?: string }
}

export type NetworkCardPresentationProps = {
  borderRadius?: number
  title: string
  description?: string | null
  logoNetwork: NetworkLogoFragment
  /** Data URL or blob URL for draft logo (wizard preview). */
  logoPreviewSrc?: string | null
  /** Renders at the top of the card header (e.g. disabled-network ribbon), flush with rounded corners. */
  headerTopBanner?: ReactNode
  headerAddon?: ReactNode
  stackedUsers?: NetworkCardPresentationStackedUser[]
  membershipTotalCount?: number
  membershipOtherLabel?: ReactNode
  /** Stretch inside grid rows (link + body fill remaining height). */
  fillHeight?: boolean
}

export function NetworkCardPresentation({
  borderRadius,
  title,
  description,
  logoNetwork,
  logoPreviewSrc,
  headerTopBanner,
  headerAddon,
  stackedUsers,
  membershipTotalCount,
  membershipOtherLabel,
  fillHeight,
}: NetworkCardPresentationProps) {
  const cardBg = useColorModeValue('gray.50', 'gray.800')
  const overlayBg = useColorModeValue('whiteAlpha.700', 'blackAlpha.700')
  const headerBg = useColorModeValue('gray.100', 'gray.700')

  const showStacked =
    (stackedUsers != null && stackedUsers.length > 0) ||
    (membershipTotalCount != null && membershipTotalCount > 0)

  return (
    <Card.Root
      borderRadius={borderRadius}
      p={0}
      rounded="2xl"
      bg={cardBg}
      overflow={headerTopBanner ? 'hidden' : undefined}
      h={fillHeight ? 'full' : undefined}
      minH={fillHeight ? 0 : undefined}
      flex={fillHeight ? 1 : undefined}
      display={fillHeight ? 'flex' : undefined}
      flexDirection={fillHeight ? 'column' : undefined}
    >
      <Card.Header
        roundedTop="2xl"
        p={0}
        bg={headerBg}
        gap={0}
        display="flex"
        flexDirection="column"
      >
        {headerTopBanner}
        <Box
          bg={overlayBg}
          w="100%"
          roundedTop={headerTopBanner ? 'none' : '2xl'}
          display="flex"
          flexDirection="row"
          alignItems="center"
          gap={5}
          p={5}
        >
          <NetworkLogo
            network={logoNetwork}
            previewSrc={logoPreviewSrc}
            size="xl"
            flexShrink={0}
          />
          <VStack alignItems="flex-start" gap={1} flex={1} minW={0}>
            <Heading size="lg" lineClamp={2}>
              {title}
            </Heading>
            {headerAddon}
          </VStack>
        </Box>
      </Card.Header>
      <Card.Body
        flex={fillHeight ? 1 : undefined}
        minH={fillHeight ? 0 : undefined}
      >
        <VStack
          p="6"
          alignItems="flex-start"
          rounded="2xl"
          gap={3}
          w="full"
          flex={fillHeight ? 1 : undefined}
          justifyContent="flex-start"
        >
          {description ? (
            <Box fontSize="sm" color="fg.muted" lineClamp={3} w="full">
              {description}
            </Box>
          ) : null}
          {showStacked ? (
            <UserStackedAvatars
              users={stackedUsers}
              totalCount={membershipTotalCount}
              otherLabel={membershipOtherLabel}
            />
          ) : null}
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}
